import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/adminApi";
import { BrandBand, SectionHeading } from "@/components/vendor/Brand";
import { VendorInfoSection } from "@/components/vendor/VendorInfoSection";
import { RoomPackagesTable } from "@/components/vendor/RoomPackagesTable";
import { ServicesTable } from "@/components/vendor/ServicesTable";
import { VendorSuccess } from "@/components/vendor/VendorSuccess";
import "@/styles/hausa-vendor.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+()\-.\sxX]+$/;
const blankVendor = () => ({ company_name: "", contact_name: "", email: "", phone: "" });

const validate = (v) => {
  const e = {};
  if (!v.company_name.trim()) e.company_name = "Company name is required";
  if (!v.contact_name.trim()) e.contact_name = "Contact name is required";
  if (!v.email.trim()) e.email = "Email is required";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "Enter a valid email address";
  const p = v.phone.trim();
  if (!p) e.phone = "Phone is required";
  else if (!PHONE_RE.test(p) || p.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone number";
  return e;
};

const serverFieldErrors = (detail) => {
  if (!Array.isArray(detail)) return {};
  return Object.fromEntries(detail.filter((d) => d.loc?.[1] === "vendor" && d.loc[2]).map((d) => [d.loc[2], d.msg.replace(/^Value error, /, "")]));
};

const makeForm = (catalog) => ({
  key: crypto.randomUUID(),
  vendor: blankVendor(),
  rooms: Object.fromEntries(catalog.rooms.map((r) => [r.id, { rate: "", approved: false }])),
  services: Object.fromEntries(catalog.services.map((s) => [s.id, ""])),
  notes: "",
  website: "",
});

export default function VendorPricing() {
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState(null);
  const resetForm = () => setForm(makeForm(catalog));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Vendor Pricing Review | hausA Interior Design Studio";
    axios.get(`${API}/vendor-pricing/catalog`)
      .then((r) => { setForm(makeForm(r.data)); setCatalog(r.data); })
      .catch(() => setLoadError(true));
  }, []);

  const setVendor = (k, v) => {
    setForm((f) => ({ ...f, vendor: { ...f.vendor, [k]: v } }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const setRoom = (id, patch) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [id]: { ...f.rooms[id], ...patch } } }));
  const onApprove = (id, on) => setRoom(id, on ? { approved: true, rate: "" } : { approved: false });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const v = validate(form.vendor);
    setErrors(v);
    setSubmitError("");
    if (Object.keys(v).length) {
      document.getElementById("vendor-info-anchor")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/vendor-pricing/submissions`, {
        idempotency_key: form.key,
        vendor: Object.fromEntries(Object.entries(form.vendor).map(([k, val]) => [k, val.trim()])),
        rooms: catalog.rooms.map((r) => ({ id: r.id, approved: form.rooms[r.id].approved, rate: form.rooms[r.id].approved ? null : form.rooms[r.id].rate || null })),
        services: catalog.services.map((s) => ({ id: s.id, rate: form.services[s.id] || null })),
        notes: form.notes || null,
        website: form.website || null,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors(serverFieldErrors(detail));
      setSubmitError(err.response ? formatApiError(detail) : "Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { resetForm(); setResult(null); setErrors({}); setSubmitError(""); };

  return (
    <div className="hx" data-testid="vendor-pricing-page">
      <BrandBand />
      <main className="hx-container hx-doc">
        <h1 className="hx-title" data-testid="vendor-form-title">{catalog?.title || "Furniture Assembly | Vendor Pricing Review"}</h1>
        {catalog && <p className="hx-subtitle" data-testid="vendor-form-subtitle">{catalog.subtitle}</p>}
        {loadError && <p className="hx-alert" style={{ marginTop: 24 }} data-testid="vendor-catalog-error">The form could not be loaded. Please refresh the page.</p>}
        {result && <VendorSuccess result={result} onReset={reset} />}
        {catalog && form && !result && (
          <form onSubmit={onSubmit} noValidate className="hx-fade" data-testid="vendor-pricing-form">
            <div id="vendor-info-anchor" />
            <VendorInfoSection vendor={form.vendor} errors={errors} onChange={setVendor} />
            <RoomPackagesTable rooms={catalog.rooms} values={form.rooms} onRate={(id, rate) => setRoom(id, { rate })} onApprove={onApprove} />
            <ServicesTable catalog={catalog} values={form.services} onRate={(id, val) => setForm((f) => ({ ...f, services: { ...f.services, [id]: val } }))} />
            <section className="hx-section" data-testid="assumptions-section">
              <SectionHeading>Quote Assumptions / Exclusions</SectionHeading>
              <p className="hx-static" data-testid="assumptions-text">{catalog.assumptions}</p>
            </section>
            <section className="hx-section">
              <SectionHeading>Vendor Notes</SectionHeading>
              <textarea
                className="hx-input"
                style={{ marginTop: 16 }}
                rows={5}
                maxLength={5000}
                placeholder="Exclusions, minimum charges, service area, pricing considerations…"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                aria-label="Vendor notes"
                data-testid="vendor-notes-textarea"
              />
            </section>
            <div className="hx-honeypot" aria-hidden="true">
              <label htmlFor="vendor-website">Website</label>
              <input id="vendor-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} data-testid="vendor-honeypot-input" />
            </div>
            <div className="hx-submit-row">
              {submitError && <p className="hx-alert" role="alert" data-testid="vendor-submit-error">{submitError}</p>}
              <button type="submit" className="hx-btn" disabled={submitting} data-testid="vendor-submit-button">
                {submitting ? "Submitting…" : "Submit Pricing Review"}
              </button>
            </div>
          </form>
        )}
      </main>
      <footer className="hx-footer">
        <div className="hx-container">hausA Interior Design Studio · by Flex Living</div>
      </footer>
    </div>
  );
}
